import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, MoreVertical, Plus, Search, UserMinus, Users } from 'lucide-react';
import { Button } from 'components/ui/button';
import { Avatar } from 'components/ui/avatar';
import { StatusBadge } from 'components/ui/status-badge';
import EmptyState from 'components/ui/EmptyState';
import { TableSearch } from 'components/ui/data-table';
import { TablePager } from 'components/ui/pagination';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from 'components/ui/dropdown-menu';
import { ConfirmDialog } from 'components/ui/confirm-dialog';
import { usePaginationParams } from 'hooks/use-pagination-params';
import { useSearchParamState } from 'hooks/use-search-param-state';
import * as TeamsModule from 'modules/teams';
import type { TeamDetail, TeamMember } from 'modules/teams/types';
import { useAuthContext } from 'modules/auth';
import { downloadStudentsCsv, groupStudents } from './groupRoster';

const DASH = '—';

const Dash: React.FC = () => <span className="text-muted-foreground/60">{DASH}</span>;

export interface MembersGradebookProps {
  team: TeamDetail;
  /** Offered on an empty roster when the viewer may invite; the page header carries the same action. */
  onInvite?: () => void;
}

/**
 * The group's student table: search, export, and per-student columns. The teams API
 * returns identity, role and join date only, so Bajarildi / O'rtacha / Faollik /
 * O'zgarish render "—" until per-student progress exists.
 */
export const MembersGradebook: React.FC<MembersGradebookProps> = ({ team, onInvite }) => {
  const { t } = useTranslation();
  const { state: authState } = useAuthContext();
  const userId = authState.user?.id;
  const removeMember = TeamsModule.Hooks.useRemoveMember();
  const { input: search, setInput: setSearch } = useSearchParamState();
  const [removeTarget, setRemoveTarget] = useState<TeamMember | null>(null);
  const { page, pageSize, setPage, setPageSize } = usePaginationParams({ defaultPage: 1, defaultPageSize: 10 });

  const students = useMemo(() => groupStudents(team), [team]);
  // Removing a student stays with the group's own admins, as before.
  const isTeamAdmin = team.members.some((m) => m.userId === userId && m.role === 'admin');
  const nameOf = (m: TeamMember) => m.user?.name || t('teams.unknown_user');

  const needle = search.trim().toLocaleLowerCase();
  const filtered = useMemo(
    () =>
      needle
        ? students.filter(
            (m) =>
              (m.user?.name ?? '').toLocaleLowerCase().includes(needle) ||
              (m.user?.email ?? '').toLocaleLowerCase().includes(needle),
          )
        : students,
    [students, needle],
  );

  const pageCount = Math.max(1, Math.ceil(filtered.length / pageSize));
  // A removal or a narrower search can leave the page past the end.
  useEffect(() => {
    if (page > pageCount) setPage(pageCount);
  }, [page, pageCount, setPage]);
  const pageRows = filtered.slice((page - 1) * pageSize, page * pageSize);

  const changeSearch = (value: string) => {
    setSearch(value);
    setPage(1);
  };

  const handleConfirmRemove = async () => {
    if (!removeTarget) return;
    try {
      // The mutation refreshes the group query and toasts either way.
      await removeMember.mutateAsync({ teamId: team.id, userId: removeTarget.userId });
      setRemoveTarget(null);
    } catch {
      /* toast handled by hook; the dialog stays open to retry */
    }
  };

  const renderBody = () => {
    if (students.length === 0) {
      return (
        <EmptyState
          icon={<Users size={28} />}
          title={t('group_no_members')}
          description={t('group_code_hint')}
          action={
            onInvite
              ? { label: t('group_invite_students'), icon: <Plus className="h-4 w-4" />, onClick: onInvite }
              : undefined
          }
        />
      );
    }
    if (filtered.length === 0) {
      return (
        <EmptyState
          icon={<Search size={28} />}
          title={t('teams.no_members_match')}
          description={t('try_adjusting_your_search_or_filters')}
          secondaryAction={{ label: t('clear_search'), onClick: () => changeSearch('') }}
        />
      );
    }
    return (
      <Table className="min-w-[760px] table-fixed">
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <TableHead>{t('gradebook.col_student')}</TableHead>
            <TableHead className="w-[136px]">{t('status')}</TableHead>
            <TableHead className="w-[150px]">{t('gradebook.col_completed')}</TableHead>
            <TableHead className="w-[100px] text-right">{t('gradebook.col_avg')}</TableHead>
            <TableHead className="w-[130px]">{t('gradebook.col_last_active')}</TableHead>
            <TableHead className="w-[104px]">{t('gradebook.col_trend')}</TableHead>
            <TableHead className="w-14">
              <span className="sr-only">{t('more_options')}</span>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {pageRows.map((m) => (
            <TableRow key={m.userId} className="h-[52px]">
              <TableCell className="max-w-0 py-2">
                <div className="flex min-w-0 items-center gap-2.5">
                  <Avatar name={m.user?.name} src={m.user?.avatarUrl} size="sm" className="h-7 w-7 text-[11px]" />
                  <span className="truncate text-[13.5px] font-medium text-foreground">{nameOf(m)}</span>
                </div>
              </TableCell>
              <TableCell className="py-2">
                <StatusBadge tone="success">{t('group_status_active')}</StatusBadge>
              </TableCell>
              <TableCell className="py-2 text-[13px] tabular-nums">
                <Dash />
              </TableCell>
              <TableCell className="py-2 text-right text-[13.5px] tabular-nums">
                <Dash />
              </TableCell>
              <TableCell className="py-2 text-[13px]">
                <Dash />
              </TableCell>
              <TableCell className="py-2">
                <Dash />
              </TableCell>
              <TableCell className="py-0 text-right">
                {isTeamAdmin && m.userId !== userId && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        type="button"
                        aria-label={t('more_options')}
                        className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      >
                        <MoreVertical className="h-4 w-4" aria-hidden />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        className="gap-2 text-destructive focus:text-destructive"
                        onClick={() => setRemoveTarget(m)}
                      >
                        <UserMinus size={14} />
                        {t('group_detail_remove_student')}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-2">
        <TableSearch
          value={search}
          onChange={changeSearch}
          placeholder={t('group_search_students')}
          aria-label={t('group_search_students')}
          className="sm:w-[260px]"
        />
        <Button
          variant="outline"
          size="sm"
          className="ml-auto gap-1.5"
          onClick={() => downloadStudentsCsv(team, t('teams.unknown_user'))}
          disabled={students.length === 0}
        >
          <Download className="h-4 w-4" />
          {t('group_export')}
        </Button>
      </div>

      <div className="overflow-hidden rounded-lg border border-border bg-card">
        {renderBody()}
        {filtered.length > 0 && (
          <div className="border-t border-border px-4 py-3">
            <TablePager
              page={page}
              pageCount={pageCount}
              pageSize={pageSize}
              total={filtered.length}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              noun={t('group_detail_pager_noun_students')}
            />
          </div>
        )}
      </div>

      <ConfirmDialog
        open={removeTarget !== null}
        onOpenChange={(open) => !open && setRemoveTarget(null)}
        title={t('teams.remove_member_title')}
        description={t('group_detail_remove_desc', {
          name: removeTarget?.user?.name || removeTarget?.user?.email || t('teams.unknown_user'),
        })}
        confirmLabel={t('group_detail_remove_student')}
        cancelLabel={t('cancel')}
        destructive
        loading={removeMember.isPending}
        onConfirm={handleConfirmRemove}
      />
    </div>
  );
};

export default MembersGradebook;
